import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, FileText } from "lucide-react";

const DEFAULT_NOTE = "Welcome to MentorAI. Your portfolio reflects simulated performance during our revalidation stage. Explore your dashboard, review statements, and message our team anytime.";

export default function WelcomeEditor() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [originalNote, setOriginalNote] = useState("");

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users?status=active");
      const clients = (data.users || []).filter((u) => u.role !== "admin");
      setUsers(clients);
    } catch (e) { console.error("Fetch users error:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSelect = (userId) => {
    setSelectedId(userId);
    const u = users.find((x) => x.user_id === userId);
    const currentNote = u?.welcome_note || DEFAULT_NOTE;
    setNote(currentNote);
    setOriginalNote(currentNote);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.post(`/admin/users/${selectedId}/note`, { note: note.trim() });
      setOriginalNote(note.trim());
      toast.success("Welcome note updated.");
      // Refresh users to reflect the updated note
      fetchUsers();
    } catch (e) { console.error("Save note error:", e); toast.error("Failed to save note."); }
    finally { setSaving(false); }
  };

  const selectedUser = users.find((u) => u.user_id === selectedId);
  const isDirty = note.trim() !== originalNote;

  return (
    <div data-testid="admin-welcome-editor" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome Note Editor</h1>
        <p className="mt-1 text-sm text-muted-foreground">Personalise the welcome message shown on each client's dashboard.</p>
      </div>

      <Card className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm p-5 sm:p-6 max-w-2xl">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Client</Label>
              <Select value={selectedId} onValueChange={handleSelect}>
                <SelectTrigger data-testid="welcome-client-select" className="rounded-xl">
                  <SelectValue placeholder="Choose a client…" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} — {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedId && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Welcome Note for {selectedUser?.name?.split(" ")[0]}</Label>
                    <span className="text-xs text-muted-foreground">{note.length} chars</span>
                  </div>
                  <Textarea
                    data-testid="welcome-note-textarea"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="min-h-[140px] rounded-xl text-sm leading-relaxed"
                    placeholder="Write a personalised welcome message…"
                  />
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <FileText className="size-3.5" /> This message is displayed at the top of the client's dashboard.
                  </p>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Button data-testid="welcome-reset-button" variant="ghost" size="sm" className="text-xs rounded-lg" disabled={!isDirty}
                    onClick={() => setNote(originalNote)}>Reset</Button>
                  <Button data-testid="welcome-save-button" onClick={handleSave} disabled={saving || !isDirty}
                    className="rounded-xl bg-brand text-white hover:bg-[#0B8E8D] gap-1.5">
                    <Save className="size-4" /> {saving ? "Saving…" : "Save Note"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
